import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { MapPin } from "lucide-react";

const uuid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

export const AddressAutocomplete = ({ value = "", onChange, onSelect, placeholder = "Saisir une adresse", testid = "address-input", country = "fr", className = "" }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const session = useRef(uuid());
  const skip = useRef(false);
  const typed = useRef(false);

  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    if (!typed.current) return; // ne pas suggérer pour une valeur déjà saisie/validée
    const v = (value || "").trim();
    if (v.length < 3) { setItems([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/address/suggest", { params: { q: v, session_token: session.current, country } });
        setItems(data.suggestions || []); setOpen(true);
      } catch { setItems([]); setOpen(false); }
      finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(t);
  }, [value, country]);

  const choose = async (item) => {
    skip.current = true;
    typed.current = false;
    try {
      const { data } = await api.post("/address/retrieve", { mapbox_id: item.mapbox_id, session_token: session.current, country });
      onChange?.(data.formatted_address || item.full_address || item.name || "");
      onSelect?.(data);
    } catch {
      onChange?.(item.full_address || item.name || "");
    }
    setItems([]); setOpen(false);
    session.current = uuid();
  };

  return (
    <div className="relative">
      <Input data-testid={testid} className={className} placeholder={placeholder} value={value || ""} autoComplete="off"
        onChange={(e) => { typed.current = true; onChange?.(e.target.value); }} onFocus={() => items.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && (items.length > 0 || loading) && (
        <div data-testid="address-suggestions" className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl">
          {loading && <div className="px-3 py-2 text-xs text-zinc-500">Recherche…</div>}
          {items.map((s) => (
            <button key={s.mapbox_id} type="button" data-testid={`address-option-${s.mapbox_id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(s)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800">
              <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#FF6A00]" />
              <span><span className="font-medium">{s.name}</span>{s.place_formatted && <span className="block text-xs text-zinc-500">{s.place_formatted}</span>}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
